import { Alert, Badge, Box, Button, Collapse, Group, Kbd, Text, Textarea } from '@mantine/core';
import ResponsiveRow from '@/shared/responsive/ResponsiveRow';
import { useBreakpointUp } from '@/shared/responsive/useBreakpoint';
import { ChevronDown, Sparkles, Target } from 'lucide-react';
import { useState, type KeyboardEvent, type ReactElement, type ReactNode } from 'react';
import { SurfaceCard } from '@/design/SurfaceCard';

export interface AiAssistExample {
    label: string;
    value: string;
}

interface AiAssistComposerProps {
    /** Branded heading; defaults to "AI Assist". */
    title?: string;
    /** One-line description under the heading. */
    subtitle: string;
    /** Optional "Drafting for X" scope pill on the right of the header. Omit/null to hide. */
    scopeLabel?: string | null;
    promptText: string;
    placeholder?: string;
    generating: boolean;
    error?: string | null;
    /** Clickable example prompts that prefill the composer. */
    examples?: AiAssistExample[];
    canGenerate: boolean;
    /** When true the composer body can be toggled open/closed from the header. */
    collapsible?: boolean;
    /** Initial collapsed state when `collapsible` (defaults to expanded). */
    defaultCollapsed?: boolean;
    onPromptChange: (value: string) => void;
    onGenerate: () => void;
    /** Results slot rendered below the composer (suggestion cards, capabilities, etc.). */
    children?: ReactNode;
}

/**
 * The shared AI-assist composer shell: a branded header, a natural-language
 * prompt with ⌘/Ctrl+↵ submit, example prompts, a Generate button, and an error
 * surface. Screens supply their own results UI via `children` because the
 * outputs differ (per-field suggestion cards vs. a single drafted form).
 */
export default function AiAssistComposer({
    title = 'AI Assist',
    subtitle,
    scopeLabel,
    promptText,
    placeholder = 'Describe a behavior to classify — e.g. flag premium-model use on simple formatting tasks and score how wasteful it was 1–10.',
    generating,
    error,
    examples,
    canGenerate,
    collapsible = false,
    defaultCollapsed = false,
    onPromptChange,
    onGenerate,
    children,
}: AiAssistComposerProps): ReactElement {
    const [collapsed, setCollapsed] = useState(collapsible && defaultCollapsed);
    const expanded = !collapsible || !collapsed;
    const toggle = (): void => setCollapsed((c) => !c);
    const isWide = useBreakpointUp('sm');

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
        if ((event.metaKey || event.ctrlKey) && event.key === 'Enter' && canGenerate) {
            event.preventDefault();
            onGenerate();
        }
    };

    return (
        <SurfaceCard $padding="lg" style={{ borderTop: '3px solid var(--mantine-primary-color-filled)' }}>
            <Group
                justify="space-between"
                align="center"
                mb={expanded ? 'md' : 0}
                wrap="nowrap"
                onClick={collapsible ? toggle : undefined}
                style={{ cursor: collapsible ? 'pointer' : undefined }}
            >
                <Group gap="sm" wrap="nowrap">
                    <Box
                        style={{
                            width: 34,
                            height: 34,
                            borderRadius: 'var(--bm-radius)',
                            background: 'var(--mantine-primary-color-filled)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            boxShadow: '0 2px 6px var(--bm-color-brand-border)',
                        }}
                    >
                        <Sparkles size={18} color="white" />
                    </Box>
                    <Box style={{ minWidth: 0 }}>
                        <Group gap={6} align="center">
                            <Text fw={700} size="sm">
                                {title}
                            </Text>
                            <Badge size="xs" variant="light" radius="sm">
                                beta
                            </Badge>
                        </Group>
                        <Text size="xs" c="dimmed" visibleFrom="sm">
                            {subtitle}
                        </Text>
                    </Box>
                </Group>
                <Group gap="sm" align="center" wrap="nowrap" style={{ flexShrink: 0 }}>
                    {scopeLabel !== undefined && (
                        <Group gap={6} align="center" wrap="nowrap" style={{ color: 'var(--mantine-primary-color-filled)' }}>
                            <Target size={13} />
                            <Text size="xs" fw={700} truncate>
                                {scopeLabel ? `Drafting for ${scopeLabel}` : 'Select a scope'}
                            </Text>
                        </Group>
                    )}
                    {collapsible && (
                        <ChevronDown
                            size={18}
                            color="var(--bm-color-text-dimmed)"
                            style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease' }}
                        />
                    )}
                </Group>
            </Group>

            <Collapse in={expanded}>
                <Textarea
                    value={promptText}
                    onChange={(e) => onPromptChange(e.currentTarget.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    autosize
                    minRows={3}
                    maxRows={6}
                    styles={{ input: { fontSize: 14 } }}
                />

                <ResponsiveRow mt="sm">
                    <Group gap={6} wrap="wrap" style={{ flex: 1, minWidth: 0 }}>
                        {examples && examples.length > 0 && (
                            <>
                                <Text size="xs" c="dimmed" fw={600}>
                                    Try:
                                </Text>
                                {examples.map((example) => (
                                    <Button
                                        key={example.label}
                                        size={isWide ? 'compact-xs' : 'sm'}
                                        variant="default"
                                        onClick={() => onPromptChange(example.value)}
                                        disabled={generating}
                                        styles={{ label: { fontWeight: 500 } }}
                                    >
                                        {example.label}
                                    </Button>
                                ))}
                            </>
                        )}
                    </Group>
                    <Group gap="xs" wrap="nowrap" justify="flex-end" style={{ flexShrink: 0 }}>
                        <Text size="xs" c="dimmed" visibleFrom="md">
                            <Kbd size="xs">⌘</Kbd> <Kbd size="xs">↵</Kbd>
                        </Text>
                        <Button
                            leftSection={<Sparkles size={15} />}
                            onClick={onGenerate}
                            loading={generating}
                            disabled={!canGenerate}
                            fullWidth={!isWide}
                        >
                            Generate
                        </Button>
                    </Group>
                </ResponsiveRow>

                {error && (
                    <Alert color="red" mt="md" title="Generation failed">
                        {error}
                    </Alert>
                )}

                {children}
            </Collapse>
        </SurfaceCard>
    );
}
